import React, { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { getKdramaDetails, getKdramaVideos, getWatchProviders } from '../components/API';
import { Box, Typography, Dialog, DialogContent, DialogTitle, Tooltip, IconButton, Alert, Snackbar, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/system';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import LoadingSpinner from './LoadingSpinner';
import { BookmarkAdd, BookmarkAdded, CloseRounded, Favorite, FavoriteBorder, PlayCircle, PauseCircle } from '@mui/icons-material';
import { doc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { db, auth } from '../firebaseConfig';
import TvOffIcon from '@mui/icons-material/TvOff';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import { UserContext } from '../context/UserContext'; // Import du contexte

function KdramaDetails() {
    const { id } = useParams();
    const { userData, setUserData } = useContext(UserContext); // Utilisation du contexte

    const [kdrama, setKdrama] = useState(null);
    const [trailer, setTrailer] = useState(null);
    const [open, setOpen] = useState(false);
    const [watchProviders, setWatchProviders] = useState(null);
    const [hoverInProgress, setHoverInProgress] = useState(false);
    const [hoverToWatch, setHoverToWatch] = useState(false);
    const [hoverWatched, setHoverWatched] = useState(false);
    const [hoverFavorite, setHoverFavorite] = useState(false);

    // Snackbar
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Vérifier si le drama est déjà dans les listes utilisateur via le contexte
    const isInWatched = userData?.watchedKdramas?.some(drama => String(drama.id) === String(id));
    const isInProgress = userData?.inProgressKdramas?.some(drama => String(drama.id) === String(id));
    const isInToWatch = userData?.toWatchKdramas?.some(drama => String(drama.id) === String(id));
    const isInToFavorite = userData?.favoriteKdramas?.some(drama => String(drama.id) === String(id));

    useEffect(() => {
        const fetchKdramaDetails = async () => {
            try {
                const data = await getKdramaDetails(id);
                setKdrama(data);
            } catch (error) {
                console.error('Erreur lors de la récupération des détails du K-Drama:', error);
            }
        };

        const fetchWatchProviders = async () => {
            try {
                const data = await getWatchProviders(id);
                setWatchProviders(data);
            } catch (error) {
                console.error('Erreur lors de la recherche des services de streaming:', error);
            }
        };

        const fetchKdramaTrailer = async () => {
            try {
                const data = await getKdramaVideos(id);
                setTrailer(data[0]);
            } catch (error) {
                console.error('Erreur lors de la récupération de la bande-annonce:', error);
            }
        };

        fetchKdramaDetails();
        fetchWatchProviders();
        fetchKdramaTrailer();
    }, [id]);

    const updateUserList = async (list, action, state, successMessage) => {
        const user = auth.currentUser;
        if (!user) return;

        const userRef = doc(db, 'users', user.uid);

        const dramaData = {
            id: id,
            title: kdrama.name,
            posterUrl: kdrama.poster_path,
            state: state
        };

        const actions = {
            add: arrayUnion(dramaData),
            remove: arrayRemove(dramaData),
        };

        try {
            await updateDoc(userRef, {
                [list]: action === 'add' ? actions.add : actions.remove
            });

            setUserData(prevState => ({
                ...prevState,
                [list]: action === 'add'
                    ? [...prevState[list], dramaData]
                    : prevState[list].filter(drama => drama.id !== id)
            }));
            setMessage(successMessage);
            setOpenSnackbar(true);
        } catch (error) {
            console.error('Erreur lors de la mise à jour des listes :', error);
        }
    };

    const toggleFavorite = () => {
        if (isInToFavorite) {
            updateUserList('favoriteKdramas', 'remove', 'toFavorite', 'Retiré de la liste des dramas favoris');
        } else {
            updateUserList('favoriteKdramas', 'add', 'toFavorite', 'Ajouté à la liste des dramas favoris');
        }
    };

    const toggleWatched = () => {
        if (isInWatched) {
            updateUserList('watchedKdramas', 'remove', 'watched', 'Retiré de la liste des dramas vus');
        } else {
            updateUserList('watchedKdramas', 'add', 'watched', 'Ajouté à la liste des dramas vus');
        }
    };

    const toggleInProgress = () => {
        if (isInProgress) {
            updateUserList('inProgressKdramas', 'remove', 'inProgress', 'Retiré de la liste des dramas en cours');
        } else {
            updateUserList('inProgressKdramas', 'add', 'inProgress', 'Ajouté à la liste des dramas en cours');
        }
    };

    const toggleToWatch = () => {
        if (isInToWatch) {
            updateUserList('toWatchKdramas', 'remove', 'toWatch', 'Retiré de la liste des dramas à voir');
        } else {
            updateUserList('toWatchKdramas', 'add', 'toWatch', 'Ajouté à la liste des dramas à voir');
        }
    }

    const handleSnackbarClose = () => {
        setOpenSnackbar(false);
    };

    if (!kdrama) {
        return <LoadingSpinner size={60} color="secondary" text="Chargement des données..." />;
    }

    const releaseYear = new Date(kdrama.first_air_date).getFullYear();
    const ratingPercentage = (kdrama.vote_average / 10) * 100;

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const providerName = watchProviders && watchProviders.FR && watchProviders.FR.flatrate
        ? watchProviders.FR.flatrate[0]?.provider_name
        : 'Non disponible sur les plateformes de streaming';

    const providerLogo = watchProviders && watchProviders.FR && watchProviders.FR.flatrate
        ? `https://media.themoviedb.org/t/p/original/${watchProviders.FR.flatrate[0]?.logo_path}`
        : null;

    return (
        <Box className="kdrama-details-container" sx={{ mt: '2rem', }}>
            <Box
                className="kdrama-details"
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: "20px",
                    justifyContent: 'center',
                    padding: '20px',
                    backgroundImage: `linear-gradient(to top, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.6)), url(https://media.themoviedb.org/t/p/w1920_and_h800_multi_faces${kdrama.backdrop_path})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                <Box className="kdrama-details-img" sx={{ p: 1, display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
                    <img
                        style={{
                            border: '2px solid white',
                            borderRadius: "10px 10px 0 0",
                            width: isMobile ? '100%' : '300px',
                            height: isMobile ? 'auto' : '450px'
                        }}
                        src={`https://media.themoviedb.org/t/p/w300_and_h450_bestv2${kdrama.poster_path}`}
                        alt={kdrama.name}
                    />
                    <Box sx={{ display: 'flex', border: '2px solid white', flexDirection: 'row', borderRadius: '0 0 10px 10px', width: isMobile ? '100%' : '300px', alignItems: 'center', backgroundColor: 'rgba(0, 0, 0, 0.8)', p: 1, gap: 1, }}>
                        {providerLogo ? (
                            <>
                                <img style={{ width: '50px', height: '50px' }} src={providerLogo} alt={providerName} />
                                <Typography sx={{ color: 'white', textAlign: 'center' }}>
                                    Disponible sur {providerName}
                                </Typography>
                            </>
                        ) : (
                            <Typography sx={{ color: 'white', textAlign: 'center' }}>{providerName}</Typography>
                        )}
                    </Box>
                </Box>

                <Box className="kdrama-details-info" sx={{ p: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
                    <Box className="kdrama-details-title" sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, alignItems: isMobile ? 'center' : 'flex-start' }}>
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'white', fontFamily: 'SUSE, sans-serif' }}>
                            {kdrama.name} <span style={{ color: 'lightgray', fontSize: '2.6rem', fontFamily: 'SUSE, sans-serif' }}>({releaseYear})</span>
                        </Typography>
                        <Box sx={{ width: 70, height: 70 }}>
                            <CircularProgressbar
                                value={ratingPercentage}
                                text={`${Math.round(ratingPercentage)}%`}
                                styles={buildStyles({
                                    textColor: '#fff',
                                    pathColor: '#21d07a',
                                    trailColor: '#204529',
                                    textSize: '33px',
                                    fontWeight: 'bold',
                                })}
                            />
                        </Box>
                    </Box>

                    <Typography variant="h5" sx={{ color: 'white', fontFamily: 'SUSE, sans-serif' }}>
                        {kdrama.genres.map((genre) => genre.name).join(', ')}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ color: 'white', fontFamily: 'SUSE, sans-serif' }}>{kdrama.number_of_episodes} épisodes</Typography>
                        <Typography sx={{ color: 'white', fontFamily: 'SUSE, sans-serif' }}>{kdrama.number_of_seasons} saison(s)</Typography>
                    </Box>

                    {trailer && (
                        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center' }}>
                            <PlayCircleOutlineIcon sx={{ color: 'white', mr: 1 }} />
                            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', cursor: 'pointer', fontFamily: 'SUSE, sans-serif' }} onClick={handleClickOpen}>
                                Bande-annonce
                            </Typography>

                            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                                <CloseRounded sx={{ position: 'absolute', top: '10px', right: '10px', cursor: 'pointer', color: 'black' }} onClick={handleClose} />
                                <DialogTitle sx={{ fontFamily: 'SUSE, sans-serif' }}>Bande-annonce</DialogTitle>
                                <DialogContent>
                                    <iframe
                                        width="100%"
                                        height="500"
                                        src={`https://www.youtube.com/embed/${trailer.key}`}
                                        title="YouTube video player"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    ></iframe>
                                </DialogContent>
                            </Dialog>
                        </Box>
                    )}

                    <Box sx={{ mt: 3 }}>
                        <Typography sx={{ color: 'white', fontStyle: 'italic' }}>"{kdrama.tagline}"</Typography>
                        <Typography variant="h5" sx={{ mt: 2, color: 'white', fontFamily: 'SUSE, sans-serif' }}>Synopsis</Typography>
                        <Typography sx={{ color: 'white', fontSize: isMobile ? '1rem' : '1.2rem', fontFamily: 'SUSE, sans-serif' }}>
                            {kdrama.overview}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end', gap: 2, mt: '50px' }}>
                        <Tooltip title="Favoris">
                            <IconButton
                                onMouseEnter={() => setHoverFavorite(true)}
                                onMouseLeave={() => setHoverFavorite(false)}
                                onClick={toggleFavorite}
                                sx={{
                                    color: isInToFavorite ? '#E91E63' : '#888888',
                                    "&:hover": { color: '#FF4081' }
                                }}
                            >
                                {isInToFavorite ? (
                                    <Favorite sx={{ width: '40px', height: '40px' }} />
                                ) : (
                                    hoverFavorite ? (
                                        <Favorite sx={{ width: '40px', height: '40px' }} />
                                    ) : (
                                        <FavoriteBorder sx={{ width: '40px', height: '40px' }} />
                                    )
                                )}
                            </IconButton>
                        </Tooltip>


                        <Tooltip title="Vu">
                            <IconButton
                                onMouseEnter={() => setHoverWatched(true)}
                                onMouseLeave={() => setHoverWatched(false)}
                                onClick={toggleWatched}
                                sx={{
                                    color: isInWatched ? '#1976D2' : '#888888',
                                    "&:hover": { color: '#42A5F5' }
                                }}
                            >
                                {isInWatched ? (
                                    <LiveTvIcon sx={{ width: '40px', height: '40px' }} />
                                ) : (
                                    hoverWatched ? (
                                        <LiveTvIcon sx={{ width: '40px', height: '40px' }} />
                                    ) : (
                                        <TvOffIcon sx={{ width: '40px', height: '40px' }} />
                                    )
                                )}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="En train de regarder">
                            <IconButton
                                onMouseEnter={() => setHoverInProgress(true)} onMouseLeave={() => setHoverInProgress(false)}
                                onClick={toggleInProgress}
                                sx={{ color: isInProgress ? '#FFA000' : '#888888', "&:hover": { color: '#FFCA28' } }}
                            >
                                {isInProgress ? (
                                    <PlayCircle sx={{ width: '40px', height: '40px' }} />
                                ) : (
                                    hoverInProgress ? (
                                        <PlayCircle sx={{ width: '40px', height: '40px' }} />
                                    ) : (
                                        <PauseCircle sx={{ width: '40px', height: '40px' }} />
                                    )
                                )}
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="À voir">
                            <IconButton
                                onMouseEnter={() => setHoverToWatch(true)} onMouseLeave={() => setHoverToWatch(false)}

                                onClick={toggleToWatch}
                                sx={{
                                    color: isInToWatch ? '#388E3C' : '#888888',
                                    "&:hover": { color: '#66BB6A' }
                                }}
                            >
                                {isInToWatch ? (
                                    <BookmarkAdded sx={{ width: '40px', height: '40px' }} />
                                ) : (
                                    // Sinon on affiche BookmarkAdd (ou BookmarkAdded si déjà dans la liste)
                                    hoverToWatch ? (
                                        <BookmarkAdded sx={{ width: '40px', height: '40px' }} />
                                    ) : (
                                        <BookmarkAdd sx={{ width: '40px', height: '40px' }} />
                                    )
                                )}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Box>

            <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                    {message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default KdramaDetails;
